// Google tag (GA4 + Google Ads), allowlist only.
//
// public/index.html holds only the inert gtag() queue, so window.gtag always
// exists (the booking conversions and the mailto click call it) but nothing is
// loaded or sent by default. gtag.js is loaded here, and a page_view is sent
// by hand, only for the public marketing pages below, and only when this is the
// live site in a real visitor's browser that is not the owner's.
//
// Nothing loads or is sent: on any other host (local, previews), when
// navigator.webdriver is true (E2E), on admin, /login, /portal, /song-requests,
// /requests and 404 pages, and in a browser marked as the owner's.
//
// Owner opt out: localStorage "djr_notrack" = "1". It is set by opening
// /?notrack=1, by any admin sign in, and whenever the admin login token is
// found in the browser. It is never cleared by a sign out.

export const GA_ID = "G-T48JRK5N3N";
export const ADS_ID = "AW-11280035670";
export const TRACKED_HOST = "www.djrise.party";
export const OPT_OUT_KEY = "djr_notrack";

export const TRACKED_PATHS = [
  "/",
  "/booking",
  "/availability",
  "/corporate-events",
  "/venues",
  "/gallery",
  "/reviews",
  "/audio",
  "/sound-engineering-services",
  "/info-expect-process",
  "/privacy",
  "/about",
  "/contact",
];

const normalize = (pathname) => {
  const p = String(pathname ?? "").toLowerCase();
  return p.length > 1 ? p.replace(/\/+$/, "") || "/" : p || "/";
};

export const isTrackedPath = (pathname) => TRACKED_PATHS.includes(normalize(pathname));

const getStore = (win) => {
  try {
    return win?.localStorage ?? null;
  } catch (e) {
    return null;
  }
};

// Persistent "this is the owner's browser" flag.
export const markOwnerBrowser = (win = typeof window === "undefined" ? undefined : window) => {
  try {
    getStore(win)?.setItem(OPT_OUT_KEY, "1");
  } catch (e) {
    // storage blocked: the flag cannot be kept
  }
};

// True when the flag is set, the admin login token is present, or the page was
// opened with ?notrack=1. The last two also set the flag for next time.
export const isOwnerBrowser = (win = typeof window === "undefined" ? undefined : window) => {
  const store = getStore(win);
  try {
    if (store?.getItem(OPT_OUT_KEY) === "1") return true;
    const hasToken = Boolean(store?.getItem("token"));
    const notrack = new URLSearchParams(win?.location?.search ?? "").get("notrack") === "1";
    if (hasToken || notrack) {
      markOwnerBrowser(win);
      return true;
    }
  } catch (e) {
    // unreadable storage: not treated as the owner
  }
  return false;
};

export const trackingAllowed = (win = typeof window === "undefined" ? undefined : window) => {
  if (!win?.location || win.location.hostname !== TRACKED_HOST) return false;
  if (win.navigator?.webdriver === true) return false;
  return !isOwnerBrowser(win);
};

// Loads gtag.js once and configures both tags without their automatic page
// view: every page view is sent by trackPageView.
export const loadGtag = (win = window) => {
  if (win.__djrGtagLoaded) return;
  win.__djrGtagLoaded = true;
  const s = win.document.createElement("script");
  s.async = true;
  s.src = `https://www.googletagmanager.com/gtag/js?id=${GA_ID}`;
  win.document.head.appendChild(s);
  win.gtag("js", new Date());
  win.gtag("config", GA_ID, { send_page_view: false });
  win.gtag("config", ADS_ID, { send_page_view: false });
};

// One page_view for an allowlisted public page. Returns true when it was sent.
export const trackPageView = (location, win = typeof window === "undefined" ? undefined : window) => {
  try {
    if (!win || typeof win.gtag !== "function") return false;
    const pathname = location?.pathname ?? "";
    if (!isTrackedPath(pathname) || !trackingAllowed(win)) return false;
    // One page view per page: a query string change on the same page (the
    // booking form keeps date, time and hours in the URL) is not a new view.
    const key = normalize(pathname);
    if (win.__djrLastPageView === key) return false;
    win.__djrLastPageView = key;
    loadGtag(win);
    win.gtag("event", "page_view", {
      page_location: win.location.href,
      page_path: `${pathname}${location?.search ?? ""}`,
      page_title: win.document?.title ?? "",
    });
    return true;
  } catch (e) {
    return false; // tracking must never break navigation
  }
};
